import React, { useCallback, useEffect } from "react";
import { useForm } from "antd/es/form/Form";
import { observer } from "mobx-react";
import { Button } from "antd";
import CreateCmrStore, {
  ErrorValueType,
} from "../../../stores/cmr/CreateCmrStore";
import { Option } from "../../common/forms/Interfaces";
import Spinner from "../../common/Spinner";
import CmrForm from "./form";
import { CANCEL_BUTTON, SAVE_BUTTON } from "../../../constants/buttons";
import { SAVE_CMR_DOWNLOAD_PDF } from "../../../constants/translates";
import FormFooter from "./footer";
import { ENABLE_UPDATE_CMR_FORM } from "../../../constants/events";
import ee from "../../../services/EventEmitter";

interface Props {
  onFinish: () => void;
  clientId?: number;
  departmentId?: null | number;
  clients: Option[];
  departments: Option[];
  countries: Option[];
  hash?: string;
}

const store = new CreateCmrStore();

const Form: React.FC<Props> = ({
  onFinish,
  clientId,
  departmentId,
  clients,
  departments,
  countries,
  hash,
}) => {
  const [form] = useForm();

  useEffect(() => {
    if (!hash) {
      return;
    }

    store.cmrNumber = undefined;

    store.loadCmrNumber().finally(() => {
      form.resetFields();
    });
  }, [form, hash]);

  const onFinishHandler = useCallback(
    (values: Object): void => {
      const val = {
        department_id: departmentId,
        user_id: clientId,
        ...values,
      };

      store.create(val as Object).finally(() => {
        if (Object.keys(store.errors).length > 0) {
          return;
        }

        form.resetFields();
        onFinish();
      });
    },
    [clientId, departmentId, form, onFinish]
  );

  const onCancelHandler = useCallback(() => {
    onFinish();
  }, [onFinish]);

  const onSaveAndDownloadHandler = useCallback(() => {
    store.saveAndDownload(form.getFieldsValue() as Object).then((id) => {
      ee.emit(ENABLE_UPDATE_CMR_FORM, id);
      onFinish();
    });
  }, [form, onFinish]);

  if (!store.cmrNumber) {
    return <Spinner isActive />;
  }

  const footer = (
    <FormFooter
      elements={[
        {
          key: "save",
          element: (
            <Button type="primary" htmlType="submit">
              {SAVE_BUTTON}
            </Button>
          ),
        },

        {
          key: "cancel",
          element: (
            <Button type="default" htmlType="button" onClick={onCancelHandler}>
              {CANCEL_BUTTON}
            </Button>
          ),
        },

        {
          key: "savAndDownload",
          element: (
            <Button
              type="primary"
              htmlType="button"
              onClick={onSaveAndDownloadHandler}
            >
              {SAVE_CMR_DOWNLOAD_PDF}
            </Button>
          ),
        },
      ]}
    />
  );

  return (
    <CmrForm
      onCancel={onCancelHandler}
      clients={clients}
      departments={departments}
      errors={store.errors as { [key: string]: ErrorValueType }}
      form={form}
      initialValues={{
        cmr_number: store.cmrNumber,
        department_id: departmentId?.toString(),
        user_id: clientId,
      }}
      onFinish={onFinishHandler}
      onSaveAndDownloadPdf={onSaveAndDownloadHandler}
      countries={countries}
      footer={footer}
    />
  );
};

export default observer(Form);
